import classNames from 'classnames'
import { AddressPopup } from 'feature/addresses/components/AddressPopup/AddressPopup'
import { FormInputText } from 'feature/formComponents'

import { FormEvent, memo } from 'react'
import { useFormContext } from 'react-hook-form'

import { Button } from 'ui'
import { ButtonProps } from 'ui/Button/Button'
import { LoaderInCentalPosition } from 'widget/LoaderInCentalPosition/LoaderInCentalPosition'

import style from './EditFitterDataForm.module.scss'
import { useEditFitterDataFormUtils } from './hooks/useEditFitterDataFormUtils'
import { ChevronLeft, Person } from '@mui/icons-material'
import { LkmRegistrationStepTwo } from 'pages/LkmRegistration/components/LkmRegistrationStepTwo'
import { useGetDealerProductTypesQuery } from 'feature/registration/api/registrationLkmApi'
import { useNavigate } from 'react-router'

export type UserDataFormProps = {
  sendButton: Pick<ButtonProps, 'endIcon' | 'startIcon'> & {
    disabled?: boolean
    isLoading?: boolean
  }
  onSubmit: () => void
  fieldsWrapperClass?: string
}

export const EditFitterDataForm = memo(
  ({ onSubmit, sendButton, fieldsWrapperClass }: UserDataFormProps) => {
    const { formState, watch } = useFormContext()
    const navigate = useNavigate()
    const {
      data: productTypes = [],
      isError: isProductTypesError,
      isLoading: isProductTypesLoading,
    } = useGetDealerProductTypesQuery()

    const { viewMode, setViewMode, isLoading } = useEditFitterDataFormUtils()

    const productTypesValue = watch('productTypes')
    const serviceRegionsValue = watch('serviceRegions')

    const isStepTwoValid =
      formState.isValid &&
      productTypesValue.length > 0 &&
      Array.isArray(serviceRegionsValue) &&
      serviceRegionsValue.length > 0

    const handleSubmit = (e: FormEvent) => {
      e.preventDefault()
      onSubmit()
    }

    const handleBackButtonClick = () => {
      navigate('/settings')
    }

    if (isProductTypesLoading || isLoading) {
      return <LoaderInCentalPosition />
    }

    switch (viewMode) {
      case 'popup':
        return <AddressPopup setViewMode={setViewMode} />
      default: // 'form'
        return (
          <div className={style.formWrapper}>
            <form className={style.regForm} noValidate onSubmit={handleSubmit}>
              <div className={style.inputsWrapper}>
                <div
                  className={classNames(style.textFields, {
                    fieldsWrapperClass,
                  })}
                >
                  <div className={style.formHeader}>
                    <div className={style.title}>
                      <Person />
                      <span className='h1'>Личные данные</span>
                    </div>
                    <Button
                      className={style.headerButton}
                      startIcon={<ChevronLeft />}
                      onClick={handleBackButtonClick}
                      data-testid='fitter-edit-back'
                    >
                      Назад
                    </Button>
                  </div>
                  <FormInputText
                    name='lastname'
                    inputProps={{
                      label: 'Фамилия',
                      required: true,
                      'data-testid': 'fitter-edit-lastname',
                    }}
                  />
                  <FormInputText
                    name='firstname'
                    inputProps={{
                      label: 'Имя',
                      required: true,
                      'data-testid': 'fitter-edit-firstname',
                    }}
                  />
                  <FormInputText
                    name='middlename'
                    inputProps={{
                      label: 'Отчество',
                      helperText:
                        'Необходимо указать данные в соответствии с сертификатом ДорХан',
                      'data-testid': 'fitter-edit-middlename',
                    }}
                  />
                  <FormInputText
                    name='email'
                    inputProps={{
                      label: 'E-mail',
                      required: true,
                      type: 'email',
                      'data-testid': 'fitter-edit-email',
                    }}
                  />
                </div>

                <LkmRegistrationStepTwo
                  productTypes={productTypes}
                  productTypesError={isProductTypesError}
                  isUpdateForm={true}
                />
              </div>

              <Button
                type='submit'
                endIcon={sendButton.endIcon}
                startIcon={sendButton.startIcon}
                disabled={sendButton.isLoading || !isStepTwoValid}
                size='medium'
                variant='contained'
                data-testid='fitter-edit-submit'
              >
                {sendButton.isLoading && (
                  <LoaderInCentalPosition className={style.loader} />
                )}
                Сохранить изменения
              </Button>
            </form>
          </div>
        )
    }
  },
)
